import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BellRing, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useApi, useClientOptions } from "@/hooks/useApi";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, FilterBar, DataTable, StatusBadge, ErrorBox, Section } from "@/components/common/ui-bits";
import { FormDialog } from "@/components/common/FormDialog";
import { fmtDate, fmtDateTime, money, optionList, daysLabel, RENEWAL_STATUSES, RENEWAL_TYPES, FREQUENCIES } from "@/lib/format";

export const renewalFields = (clients) => [
  { name: "client_id", label: "Cliente", type: "select", options: clients, required: true },
  { name: "service_type", label: "Tipo de servicio", type: "select", options: optionList(RENEWAL_TYPES), required: true },
  { name: "name", label: "Detalle (dominio, cuenta, plan…)", required: true },
  { name: "provider", label: "Proveedor" },
  { name: "client_price", label: "Precio de renovación al cliente (USD)", type: "number", min: 0, help: "Vacío = [PENDIENTE]; no se puede aprobar sin precio." },
  { name: "provider_cost", label: "Costo del proveedor (solo administrador)", type: "number", min: 0 },
  { name: "frequency", label: "Frecuencia", type: "select", options: optionList(FREQUENCIES), required: true, default: "Anual" },
  { name: "start_date", label: "Fecha de inicio", type: "date" },
  { name: "expiry_date", label: "Fecha de vencimiento", type: "date", required: true },
  { name: "auto_renew_authorized", label: "El cliente autorizó por escrito la renovación automática (se facturará a 30 días del vencimiento)", type: "checkbox" },
  { name: "internal_notes", label: "Notas internas", type: "textarea" },
];

const Sum = ({ label, value, testid }) => (
  <div className="card-box p-4"><p className="text-xs text-slate-500">{label}</p><p className="num mt-1 text-2xl font-semibold text-slate-900" data-testid={testid}>{value}</p></div>
);

function RecentAlerts() {
  const { data } = useApi("/renewal-alerts");
  return (
    <Section title="Alertas recientes" className="mt-6" testid="renewal-alerts-panel">
      {!(data || []).length ? <p className="text-sm text-slate-400">Sin alertas todavía. Se revisan cada día a las 8:00 (America/Chicago).</p> : (
        <ul className="divide-y divide-slate-100 text-sm">{data.slice(0, 8).map((a) => (
          <li key={a.id} className="flex flex-wrap items-center justify-between gap-2 py-2" data-testid={`renewal-alert-${a.id}`}>
            <span><b>{a.service_type}</b> · {a.name} — {a.client_name}<span className="block text-xs text-slate-500">{daysLabel(a.days_left)} · {fmtDateTime(a.at)}</span></span>
            <StatusBadge status={a.email_status} />
          </li>))}</ul>)}
    </Section>
  );
}

export default function Renewals() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const portal = user.role === "client";
  const [f, setF] = useState({});
  const { data, loading, error, reload } = useApi("/renewals", f);
  const clients = useClientOptions();
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const rows = data || [];
  const count = (s) => rows.filter((r) => r.status === s).length;
  const runAlerts = async () => {
    try { const r = await api.post("/renewals/run-alerts"); toast.success(`${r.data.alerts} alerta(s) nuevas · ${r.data.auto_invoices} factura(s) automáticas`); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title={portal ? "Mis renovaciones" : "Renovaciones"} subtitle="Ningún servicio se renueva sin la autorización del cliente. Al aprobar se genera la factura; la renovación se completa al recibir el pago."
        actions={isAdmin && <>
          <Button variant="outline" onClick={runAlerts} data-testid="run-alerts-button"><BellRing className="mr-2 h-4 w-4" />Revisar alertas ahora</Button>
          <Button onClick={() => setCreating(true)} data-testid="new-renewal-button"><Plus className="mr-2 h-4 w-4" />Nueva renovación</Button>
        </>} />
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Sum label="Próximas a vencer (30 días)" value={count("Próxima a vencer")} testid="renewals-sum-soon" />
        <Sum label="Pendientes de aprobación" value={count("Pendiente de aprobación")} testid="renewals-sum-pending" />
        <Sum label="Vencidas" value={count("Vencida")} testid="renewals-sum-expired" />
      </div>
      <FilterBar values={f} onChange={setF} testid="renewals-filters" filters={[
        { key: "status", label: "Estado", options: optionList(RENEWAL_STATUSES) }, { key: "service_type", label: "Tipo", options: optionList(RENEWAL_TYPES) },
        ...(portal ? [] : [{ key: "client_id", label: "Cliente", options: clients }])]} />
      <ErrorBox text={error} />
      <DataTable testid="renewals-table" quickView={(r) => `${r.service_type}: ${r.name}`} loading={loading} rows={data} empty="No hay renovaciones." onRowClick={(r) => navigate(`${portal ? "/portal/renovaciones" : "/renovaciones"}/${r.id}`)}
        columns={[
          { key: "service_type", label: "Servicio", render: (r) => <span><b>{r.service_type}</b><span className="block text-xs text-slate-500">{r.name}</span></span> },
          ...(portal ? [] : [{ key: "client_name", label: "Cliente" }]),
          { key: "expiry_date", label: "Vence", render: (r) => <span>{fmtDate(r.expiry_date)}<span className="block text-xs text-slate-500">{daysLabel(r.days_left)}</span></span> },
          { key: "frequency", label: "Frecuencia" },
          { key: "client_price", label: "Precio", render: (r) => <span className="num">{money(r.client_price)}</span> },
          ...(isAdmin ? [{ key: "margin", label: "Margen", render: (r) => <span className="num">{r.margin == null ? "—" : money(r.margin)}</span> }] : []),
          { key: "status", label: "Estado", render: (r) => <StatusBadge status={r.status} /> },
        ]} />
      {!portal && <RecentAlerts />}
      {isAdmin && <FormDialog open={creating} onOpenChange={setCreating} title="Nueva renovación" testid="renewal-form" wide fields={renewalFields(clients)}
        onSubmit={async (v) => { const r = await api.post("/renewals", v); toast.success("Renovación registrada"); navigate(`/renovaciones/${r.data.id}`); }} />}
    </div>
  );
}
